import { useAppSelector } from "@litres-core/store";
import { getTestGroup } from "@litres/utils";
import { useLocation } from "@litres-libs/global-hooks";

type AbTestGroupType = "test" | "control" | "skipped" | false;

export const AB_TESTS = [
  // Для нулевого значения в поиске
  1009,
  // Для отображения Арта в тайловом представлении на desktop
  1074,
  // Для отображения Арта в тайловом представлении на pda
  1083,
  // Для авторизации через OTP
  1128,
  // Для адаптивной сетки в жанрах
  1131,
  // Для новых моих книг на десктопе
  1149,
  // Для новых моих книг на pda
  1152,
  // Новый Платёжный Фрейм
  1153,
  // Верификация email по OTP при регистрации
  // UPD: завершен
  1159,
  // Показ формы Unlimit в платёжном Фрейме
  1163,
  // Показ fallback версии Лэндинга Подписки
  1165,
  // Показ Витрины Аудиокниг
  1190,
  // Показ Витрины Подкастов
  1189,
  // Кнопка для перехода на арт в приложении
  1197, 1196,
  //[WEB+PDA] Новая логика показа полного описания книги на карточке книги

  // Редирект на новую страницу Информация для входа /nextenv/me/credentials
  1215,
  // Отключать триал для пользователей, отключающих автопродление подписки
  1234,
] as const;

type AbTestConstants = (typeof AB_TESTS)[number];
/**
 * Хук, который определяет, состоит ли юзер в группе, которую
 * мы передаем вторым аргументом (по умолчанию "test")!
 * https://docs.litres.ru/pages/viewpage.action?pageId=121559376
 * @param {AbTestConstants | AbTestConstants[]} tests - Одна константа A/B-теста или массив констант A/B-тестов.
 * @param {AbTestGroupType} [group="test"] - Группа A/B-теста для проверки. По умолчанию "test".
 * @returns {boolean | boolean[]} Возвращает либо логическое значение, либо массив логических значений, указывающих результаты A/B-теста.
 *
 * @example
 * // Пример использования:
 * //  const isAbTest1131 = useAbTest(1131);
 * // const [isAbTest1009, isAbTest1011] = useAbTest([1009, 1011]);
 * // console.log(isAbTest1009, isAbTest1011);
 */
export function useAbTest(
  tests: AbTestConstants,
  group?: AbTestGroupType
): boolean;
export function useAbTest(
  tests: AbTestConstants[],
  group?: AbTestGroupType
): boolean[];
export function useAbTest(
  tests: AbTestConstants | AbTestConstants[],
  group: AbTestGroupType = "test"
): boolean | boolean[] {
  const { search = "" } = useLocation();
  const abTests = useAppSelector(state => state.foundationUser.ab_tests);

  if (Array.isArray(tests)) {
    return tests.map(
      test =>
        getTestGroup(abTests, test) === group || search.includes(`test=${test}`)
    );
  } else {
    return (
      getTestGroup(abTests, tests) === group || search.includes(`test=${tests}`)
    );
  }
}

// Может содержать только строки т.к. вариокуб отдает только их
export type VarioqubTests =
  /* тест пэйвола подписки на главной */
  | "subscription_paywall"
  /* тест на отображение растяжки в шапке на мобилках */
  | "banner_header_pda"
  /* тест на отображение растяжки в шапке в вебе */
  | "banner_header_www"
  /* тест на отображение баннера в поиске */
  | "banner_search"
  /* тест на отображение баннера в карточке книги */
  | "banner_genre"
  /* Редирект на новую страницу Информация для входа /nextenv/me/credentials */
  | "show_new_creds"
  /* Перенос кнопок из горизонтального меню */
  | "no_horizontal_line"
  /* тест на отображение артов не в продаже */
  | "available_arts_test"
  /* тест на отображение сбера вместо po */
  | "showSberPayment"
  /* тест на отображение платежного метода "Сбер Спасибо" для PPD */
  | "sberspasibo"
  /**
   * тест на включение обязательной авторизации перед входом в Старый Платёжный Фрейм для PPD
   * возможно 4 тестовых варианта отображения попапа авторизации:
   * * Вариант 1: попап, крестик, телефон — auth_form=popup-cancel-phonefirst
   * * Вариант 2: фуллскрин, нет крестика, телефон — auth_form=fullscreen-nocancel-phonefirst
   * * Вариант 3: фуллскрин, крестик, телефон — auth_form=fullscreen-cancel-phonefirst
   * * Вариант 4: фуллскрин, крестик, почта — auth_form=fullscreen-cancel-emailfirst
   */
  | "auth_form"
  /* тест на отображение дополнительных шагов про заморозку в опросе */
  | "survey_sub_freeze"
  /* тест для отоброжения функционала связоного с бонусами в wink */
  | "is_wink_enabled"
  /* ABCD тест на альтернативное отображение Sale Block'в для покупки подписки, абонемента, PPD */
  | "bookcard_saleblock"
  /*тест на показ сейлблока с бандлами */
  | "show_bundles"
  /* тест на показ нового платёжного фрейма для пополнения счёта */
  | "refill_new_frame"
  /* тест на использование paypal при оплате по модели PPD */
  | "paypal"
  /* тест онбординга RECS-18 */
  | "asking_preference"
  /**
   * тест на лучший период для рейтинга популярности
   * возможны 3 тестовых варианта сортировки:
   * * Вариант 1: недельный рейтинг — period_sort_popular=popular
   * * Вариант 2: месячный рейтинг — period_sort_popular=monthly_popular
   * * Вариант 3: годовой рейтинг — period_sort_popular=yearly_popular
   */
  | "period_sort_popular"
  /* тест на показ СБП в оплате */
  | "sbp_payment"
  /* 2 итерация теста на показ блока продажи бумажных книг из Читай-Города */
  | "paper_books"
  /*тест на выключение блюра обложек и текста */
  | "blur_disabled"
  /* Рандомизация книг в слайдерах главной NAV-2494 */
  | "with_randomize"
  /* отображение бонусов в шапке */
  | "feature_web_llt190";

/**
 * Хук, который отдает нам статус юзера из заданного аб-теста
 * @param {VarioqubTests} test - Одна константа A/B-теста
 * @returns {string} возвращает группу, в которую входит юзер в рамках эксперимента
 *
 * @example
 * // Пример использования:
 * // const isVarioqubTest1 = useGetVarioqubTestGroup("test_1");
 */
export function useGetVarioqubTestGroup(test: VarioqubTests): string;

export function useGetVarioqubTestGroup(test: VarioqubTests): string {
  const varioqubFlags = useAppSelector(
    state => state.foundationUser.varioqubTests.flags
  );
  return varioqubFlags[test];
}
/**
 * Хук, который определяет, состоит ли юзер в группе, которую
 * мы передаем вторым аргументом
 * https://docs.litres.ru/pages/viewpage.action?pageId=121559376
 * @param {VarioqubTests | VarioqubTests[]} tests - Одна константа A/B-теста или массив констант A/B-тестов.
 * @param {string} group - Группа A/B-теста для проверки.
 * @returns {boolean | boolean[]} Возвращает либо логическое значение, либо массив логических значений, указывающих результаты A/B-теста.
 *
 * @example
 * // Пример использования:
 * // const isVarioqubTest1 = useVarioqubTest("test_1", "test");
 * // const [isVarioqubTest1, isVarioqubTest2] = useVarioqubTest(["test_1", "test_2"], "test");
 */
export function useVarioqubTest(tests: VarioqubTests, group: string): boolean;
export function useVarioqubTest(
  tests: VarioqubTests[],
  group: string
): boolean[];
export function useVarioqubTest(
  // Список ключей тестов из вариокуба
  tests: VarioqubTests | VarioqubTests[],
  // Ожидаемые значения ключей
  group: string
): boolean | boolean[] {
  const varioqubFlags = useAppSelector(
    state => state.foundationUser.varioqubTests.flags
  );
  const { search = "" } = useLocation();
  return checkVarioqubFlags(varioqubFlags, tests, group, search);
}

export const checkVarioqubFlags = (
  varioqubFlags: Record<string, string>,
  tests: VarioqubTests | VarioqubTests[],
  group: string,
  search?: string
): boolean | boolean[] => {
  const isTestInSearch = (test: string) =>
    search ? search.includes(`test=${test}`) : false;

  if (Array.isArray(tests)) {
    return tests.map(
      test => varioqubFlags[test] === group || isTestInSearch(test)
    );
  }

  return varioqubFlags[tests] === group || isTestInSearch(tests);
};
